import { Notification as WCNotification } from '~components/web-components'
import { Box } from '@mui/material'
import { isAfter, isBefore } from 'date-fns'
import { useEffect, useState } from 'react'
import { matchPath, useLocation, useParams } from 'react-router-dom'
import { useIsActiveUser } from '~hooks/permissions/use-is-active-user'
import { localStorageItemKeys } from '../../constants/local-storage-items'
import {
  AlertNotification,
  NotificationVariantAPI,
} from '../../hooks/queries/use-notification-banner'
import { usePoolName } from '../../hooks/use-pool-name'
import { getNotificationsFromLocalStorage } from './notifications-banner'

type NotificationVariant = 'info' | 'error' | 'warning' | 'success' | 'pageFail'

interface NotificationProps {
  variant: NotificationVariant
  headline: string
  text: string
  onClick?: () => void
  className?: string
}

export function Notification({
  id,
  type,
  startDate,
  endDate,
  headline,
  text,
  canDismiss,
  isActive,
  pools,
  pages,
}: AlertNotification) {
  const [isActivePeriod, setIsActivePeriod] = useState<boolean>(false)
  const [isNotificationHidden, setIsNotificationHidden] =
    useState<boolean>(false)
  const [isAllowedPage, setIsAllowedPage] = useState<boolean>(true)
  const location = useLocation()
  const poolName = usePoolName()
  const { pool: poolParam } = useParams()

  useEffect(() => {
    setIsAllowedPage(isPageWithNotification({ poolName, pages, pools }))

    if (isActive) {
      const isTodayAfterStart = isAfter(new Date(), new Date(startDate))
      // though the endDate is not provided the notification should be shown
      const isTodayBeforeEnd = endDate
        ? isBefore(new Date(), new Date(endDate))
        : true
      setIsActivePeriod(isTodayAfterStart && isTodayBeforeEnd)
    }
  }, [location.pathname, poolName])

  function hideNotification() {
    setIsNotificationHidden(true)
    const hiddenNotifications = getNotificationsFromLocalStorage()
    localStorage.setItem(
      localStorageItemKeys.notificationBanners,
      JSON.stringify([...hiddenNotifications, id]),
    )
  }

  if (
    !headline ||
    !text ||
    isNotificationHidden ||
    !isActive ||
    !isActivePeriod ||
    !isAllowedPage
  ) {
    return null
  }

  const WCNotificationProps: NotificationProps = {
    headline,
    text,
    variant: type,
    onClick: canDismiss ? hideNotification : undefined,
  }

  return (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
      <WCNotification {...WCNotificationProps} />
    </Box>
  )
}

export function isPageWithNotification({
  poolName,
  pages,
  pools,
}: {
  poolName: string | undefined
  pages: string[]
  pools: string[]
}) {
  if (poolName) {
    if (!pools.includes(poolName)) {
      return false
    }
  }

  for (const page of pages) {
    const match = matchPath(page, location.pathname)

    if (match) {
      return true
    }
  }

  return false
}
